import { ArenaGameState } from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { ScoreDisplayResult, BooksToSetResult, HandScoreResult } from '../common/IGameRules';
import { IScorePresenter } from '../common/IScorePresenter';

/**
 * Presentation-only score formatter for Spades (Partnership scoring with bag tracking).
 *
 * The server is authoritative for all scoring, contracts, bids,
 * trick counts, bags, outcomes, and match state.
 *
 * This class only formats values already present in authoritative state.
 */
export class SpadesScorePresenter implements IScorePresenter {
  public formatScoreDisplay(gameState: ArenaGameState, config: GameTableConfig): ScoreDisplayResult {
    return {
      team1Label: String(gameState.team1Score ?? 0),
      team2Label: String(gameState.team2Score ?? 0),
      team1Details: `Bags: ${gameState.team1Bags ?? 0}`,
      team2Details: `Bags: ${gameState.team2Bags ?? 0}`,
      targetScoreLabel: `Target: ${config.scoring.targetScore}`
    };
  }

  public calculateBooksToSet(gameState: ArenaGameState): BooksToSetResult {
    if (gameState.booksToSet) {
      return gameState.booksToSet as BooksToSetResult;
    }
    return {
      team1: { target: 0, current: 0, needed: 0, remaining: 0 },
      team2: { target: 0, current: 0, needed: 0, remaining: 0 }
    };
  }

  public calculateHandScore(gameState: ArenaGameState, _config: GameTableConfig): HandScoreResult {
    const history = Array.isArray(gameState.scoreHistory) ? gameState.scoreHistory : [];
    const latest = history.find(h => h && h.handNumber === gameState.handNumber) || (history.length > 0 ? history[history.length - 1] : null);

    if (latest) {
      return {
        teamScores: {
          1: latest.team1Score ?? gameState.team1Score ?? 0,
          2: latest.team2Score ?? gameState.team2Score ?? 0
        },
        teamDeltas: {
          1: latest.pointsTeam1 ?? 0,
          2: latest.pointsTeam2 ?? 0
        },
        contracts: [
          {
            team: 1,
            bidAmount: 0,
            contractLabel: latest.contract ?? 'Contract',
            booksWon: gameState.team1Books ?? 0,
            neededBooks: 0,
            isMade: latest.result === 'MADE',
            isSet: Boolean(latest.result === 'SET' || gameState.isSet),
            points: latest.pointsTeam1 ?? 0,
            result: latest.result ?? (gameState.isSet ? 'SET' : 'MADE')
          }
        ],
        summary: latest.summary || gameState.lastActionMessage || '',
        isSet: Boolean(gameState.isSet || latest.result === 'SET'),
        setTeam: gameState.setTeam ?? (latest.result === 'SET' ? (latest.bidderTeam ?? 1) : null),
        celebration: null
      };
    }

    return {
      teamScores: {
        1: gameState.team1Score ?? 0,
        2: gameState.team2Score ?? 0
      },
      teamDeltas: {
        1: 0,
        2: 0
      },
      contracts: [],
      summary: gameState.lastActionMessage || '',
      isSet: Boolean(gameState.isSet),
      setTeam: gameState.setTeam ?? null,
      celebration: null
    };
  }
}
